import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Space, Spin, Typography } from "antd";
import { Helmet } from "react-helmet";
import { Link, useLocation, useParams } from "react-router-dom";
import { fetchPublicSeoArticle } from "../../../services/publicSeoArticlesApi";
import { resolvePublicSiteBootstrapCandidate } from "../../../features/vacationPackagesPublic/publicSiteIdentity";
import { appendPublicSiteToken } from "../../../utils/publicSiteUrl";

const { Paragraph, Title, Text } = Typography;

const canonicalFromSlug = (slug) => {
  const path = `/blog/${String(slug || "").trim()}`;
  if (typeof window === "undefined" || !window.location?.origin) {
    return path;
  }
  return new URL(path, window.location.origin).toString();
};

function SafeMarkdown({ markdown }) {
  const lines = String(markdown || "").split(/\r?\n/);
  return (
    <Space direction="vertical" size="middle" style={{ inlineSize: "100%" }}>
      {lines.map((line, index) => {
        const text = String(line || "");
        const trimmed = text.trim();
        if (!trimmed) {
          return <div key={`spacer-${index}`} style={{ minBlockSize: 8 }} />;
        }
        if (trimmed.startsWith("### ")) {
          return (
            <Title level={4} key={`h3-${index}`}>
              {trimmed.slice(4)}
            </Title>
          );
        }
        if (trimmed.startsWith("## ")) {
          return (
            <Title level={3} key={`h2-${index}`}>
              {trimmed.slice(3)}
            </Title>
          );
        }
        if (trimmed.startsWith("# ")) {
          return (
            <Title level={2} key={`h1-${index}`}>
              {trimmed.slice(2)}
            </Title>
          );
        }
        if (trimmed.startsWith("- ")) {
          return (
            <Paragraph key={`li-${index}`} style={{ marginBlockEnd: 6 }}>
              <span aria-hidden="true">• </span>
              {trimmed.slice(2)}
            </Paragraph>
          );
        }
        return <Paragraph key={`p-${index}`}>{trimmed}</Paragraph>;
      })}
    </Space>
  );
}

export default function PublicSeoArticlePage() {
  const { slug } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [article, setArticle] = useState(null);

  const siteCandidate = useMemo(
    () =>
      resolvePublicSiteBootstrapCandidate({
        search: location.search,
        pathname: location.pathname,
        hostname: typeof window !== "undefined" ? window.location.hostname : "",
      }),
    [location.pathname, location.search]
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    setArticle(null);

    fetchPublicSeoArticle({
      slug,
      siteId: siteCandidate.siteRef,
    })
      .then((row) => {
        if (!mounted) {
          return;
        }
        if (!row || row.status !== "published") {
          setError("ARTICLE_NOT_FOUND");
          setLoading(false);
          return;
        }
        setArticle(row);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) {
          return;
        }
        const status = err?.response?.status;
        if (status === 404) {
          setError("ARTICLE_NOT_FOUND");
        } else {
          setError("LOAD_FAILED");
        }
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [slug, siteCandidate.siteRef]);

  const pageTitle = useMemo(() => {
    if (!article) {
      return "מאמר לא נמצא";
    }
    return article.meta_title || article.title || "מאמר";
  }, [article]);

  const pageDescription = useMemo(() => {
    if (!article) {
      return "המאמר המבוקש לא זמין.";
    }
    return article.meta_description || "";
  }, [article]);

  const canonicalHref = useMemo(() => canonicalFromSlug(slug), [slug]);
  const vacationPackagesHref = useMemo(
    () => appendPublicSiteToken("/vacation-packages", siteCandidate.siteRef, { search: location.search }),
    [location.search, siteCandidate.siteRef]
  );

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div style={{ padding: 24 }}>
        <Helmet>
          <title>מאמר לא נמצא</title>
        </Helmet>
        <Alert
          type="error"
          showIcon
          message="404"
          description="המאמר לא נמצא או שאינו מפורסם."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxInlineSize: 900, margin: "0 auto" }}>
      <Helmet>
        <title>{pageTitle}</title>
        {pageDescription ? <meta name="description" content={pageDescription} /> : null}
        <link rel="canonical" href={canonicalHref} />
      </Helmet>

      <Card>
        <Space direction="vertical" size="large" style={{ inlineSize: "100%" }}>
          <div>
            <Title level={1}>{article.title}</Title>
            <Text type="secondary">{article.destination || article.category || ""}</Text>
          </div>

          <SafeMarkdown markdown={article.content_markdown} />

          <Card type="inner" title="המשך תכנון החופשה שלכם">
            <Space direction="vertical">
              <Paragraph style={{ marginBlockEnd: 0 }}>
                קבלו הצעת מחיר מותאמת אישית לחופשה הבאה שלכם.
              </Paragraph>
              <Link to={vacationPackagesHref}>
                <Button type="primary">קבלו הצעת מחיר לחופשה</Button>
              </Link>
              <Space>
                <Link to={vacationPackagesHref}>חזרה לחבילות נופש</Link>
                <Link to="/attractions">חזרה ליעדים ואטרקציות</Link>
              </Space>
            </Space>
          </Card>
        </Space>
      </Card>
    </div>
  );
}
